import type { QueryClient } from '@tanstack/react-query'
import { debounce } from 'lodash-es'

import { useMutation, useQuery, useQueryClient } from '@fv/client-core'
import type { ChatUsageState, UpdateChatFeedbackRequest } from '@fv/models/chat'

import { shipperFetch } from '@/utils/shipperFetch'

import { useRole } from '../auth/hooks'
import type { Chat, ChatListItem } from './types'

export const queryKeys = {
  all: ['chats'],
  id: (id: string) => [...queryKeys.all, id],
  usage: ['chat-usage'],
}

const postChatSessionReq = async () =>
  shipperFetch<Chat>('/chat/sessions', { method: 'POST' })

export const useCreateChatSession = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: postChatSessionReq,
    onMutate: async () => {
      // cancelling in flight queries to prevent race conditions where list query is fetched simultaneously with the create mutation
      // causing the successful create cache update to override query results
      await queryClient.cancelQueries({ queryKey: queryKeys.all })
    },
    onSuccess: chat => {
      queryClient.setQueryData(
        queryKeys.all,
        (old: ChatListItem[] | undefined) => [...(old ?? []), chat],
      )
      setChatSessionInCache(queryClient, chat)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all })
    },
  })
}
export const useChatSessions = () => {
  return useQuery<ChatListItem[]>({
    queryKey: queryKeys.all,
    queryFn: async () => shipperFetch('/chat/sessions'),
  })
}

export const useChatSession = (id: string) => {
  return useQuery<Chat>({
    queryKey: queryKeys.id(id),
    queryFn: async () => shipperFetch(`/chat/sessions/${id}`),
    enabled: !!id,
  })
}

const deleteChatReq = async (id: string) =>
  shipperFetch(`/chat/sessions/${id}`, { method: 'DELETE' })

export const useArchiveChat = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteChatReq,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all })
    },
  })
}

const putChatFeedbackReq =
  (id: string) => async (body: UpdateChatFeedbackRequest) =>
    await shipperFetch<Chat>(`/chat/sessions/${id}/feedback`, {
      method: 'PUT',
      body,
    })

export const useUpdateChatFeedback = (chatId: string) => {
  const updateChatCache = useUpdateChatCache(chatId)
  return useMutation({
    mutationFn: putChatFeedbackReq(chatId),
    onSuccess: res => {
      updateChatCache(chat => ({
        ...chat,
        ...res,
      }))
    },
  })
}

export const useUpdateChatCache = (chatId: string) => {
  const queryClient = useQueryClient()
  return (chatModifier: (chat: Chat) => Chat) => {
    queryClient.setQueryData<Chat>(queryKeys.id(chatId), chatModifier)
    queryClient.setQueryData<ChatListItem[]>(
      queryKeys.all,
      prev =>
        prev?.map(c => {
          if (c._id !== chatId) return c
          const { messages: _, ...listUpdate } = chatModifier(c as Chat)
          return { ...c, ...listUpdate }
        }) ?? prev,
    )
  }
}

export const useChatUsage = () => {
  return useQuery<ChatUsageState>({
    queryKey: queryKeys.usage,
    queryFn: async () => shipperFetch('/chat/usage'),
  })
}
const debouncedInvalidateChatUsage = debounce(
  (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.usage }),
  20_000,
)

export const useInvalidateChatUsage = () => {
  const queryClient = useQueryClient()
  return () => debouncedInvalidateChatUsage(queryClient)
}

export const useHasMoreUsage = () => {
  const { data: usage } = useChatUsage()
  if (!usage || usage.isUnlimited) return true
  return usage.percentUsed < 100
}

export const useHasAskAboutShipment = () => {
  return useRole() === 'shipper'
}

const setChatSessionInCache = (queryClient: QueryClient, chat: Chat) => {
  queryClient.setQueryData(queryKeys.id(chat._id), chat)
}
